/**
 * Email PO Dialog
 *
 * Reviewable draft of the PO email to the supplier — recipients, subject, and
 * body all editable. Sending attaches a fresh PDF of the LAST SAVED version of
 * the PO, then the backend delivers via Mailgun (replies go to the PO contact).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import InventoryService from "services/InventoryService";

const DEFAULT_TO = "kim@danican.com";
// Sends go out via Mailgun, not the sender's mailbox — cc'ing keeps a copy in their inbox
const DEFAULT_CC = "inno@sbly.com";

interface EmailPoDialogProps {
  open: boolean;
  onClose: () => void;
  poNumber: string;
}

const parseEmails = (value: string): string[] =>
  value.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export default function EmailPoDialog({ open, onClose, poNumber }: EmailPoDialogProps): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const [to, setTo] = useState(DEFAULT_TO);
  const [cc, setCc] = useState(DEFAULT_CC);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // Fresh draft each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setTo(DEFAULT_TO);
    setCc(DEFAULT_CC);
    setSubject(`Dosaze PO ${poNumber}`);
    setBody(
      `Hi Kim,\n\nPlease find attached purchase order ${poNumber}.\n\n` +
      `Could you confirm receipt and the expected production timeline?\n\nThanks,\nInno`
    );
    setErrorMessage(null);
    setSentTo(null);
  }, [open, poNumber]);

  const toEmails = parseEmails(to);
  const ccEmails = parseEmails(cc);
  const invalidEmails = [...toEmails, ...ccEmails].filter(e => !isValidEmail(e));
  const isFormValid = toEmails.length > 0 && invalidEmails.length === 0
    && subject.trim().length > 0 && body.trim().length > 0;

  const handleSend = async () => {
    if (!isFormValid) return;
    setIsSending(true);
    setErrorMessage(null);
    try {
      await service.emailPoPdf({ poNumber, to: toEmails, cc: ccEmails, subject: subject.trim(), body });
      setSentTo(toEmails.join(", "));
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Email PO {poNumber}</DialogTitle>
      <DialogContent>
        {sentTo ? (
          <Alert severity="success">Sent to {sentTo} with the PO PDF attached.</Alert>
        ) : (
          <>
            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField
                size="small"
                label="To"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                helperText="Separate multiple addresses with commas"
              />
              <TextField
                size="small"
                label="Cc"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
              />
              <TextField
                size="small"
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <TextField
                label="Message"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                multiline
                minRows={7}
              />
              {invalidEmails.length > 0 && (
                <Alert severity="warning">Not a valid email: {invalidEmails.join(", ")}</Alert>
              )}
              <Typography variant="caption" color="text.secondary">
                A fresh PDF of the last saved version of {poNumber} is attached —
                save any edits before sending. Replies go to the PO contact.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {sentTo ? (
          <Button variant="contained" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={isSending}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={!isFormValid || isSending}
            >
              {isSending ? "Sending…" : "Send email"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
